import React from 'react';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { cn } from '@/lib/utils';
import { Shield } from 'lucide-react';

export interface ClubBadge {
  id: string;
  name: string;
  logo_url: string;
  is_primary?: boolean;
}

interface ClubBadgesProps {
  clubs: ClubBadge[];
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  limit?: number;
  showTooltip?: boolean;
}

export default function ClubBadges({
  clubs,
  className,
  size = 'lg',
  limit = 10,
  showTooltip = true
}: ClubBadgesProps) {
  if (!clubs || clubs.length === 0) return null;

  // Sort so primary clubs come first
  const sortedClubs = [...clubs].sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return 0;
  });

  // Apply limit
  const displayClubs = limit > 0 ? sortedClubs.slice(0, limit) : sortedClubs;
  const hasMore = sortedClubs.length > limit && limit > 0;

  // Tailwind size classes
  const sizeClasses: Record<string, string> = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-10 w-10'
  };
  const borderClasses: Record<string, string> = {
    sm: 'border',
    md: 'border-2',
    lg: 'border-2',
    xl: 'border-2'
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {displayClubs.map((club) => (
        <Tippy
          key={club.id}
          content={showTooltip ? club.name : null}
          delay={[100, 0]}
          placement="top"
        >
          <Link to={`https://playclub.ai/dashboard/clubs/${club.id}`}>
            <div
              className={cn(
                'relative rounded-full overflow-hidden',
                borderClasses[size],
                club.is_primary
                  ? 'border-gold-400 z-10'
                  : 'border-light-300 dark:border-dark-400'
              )}
            >
              {club.logo_url ? (
                <img
                  src={club.logo_url}
                  alt={club.name}
                  className={cn('rounded-full object-cover', sizeClasses[size])}
                  onError={(e) => {
                    const target = e.currentTarget;
                    const parent = target.parentElement;
                    if (parent) {
                      const shield = document.createElement('div');
                      shield.className = cn(
                        'flex items-center justify-center bg-dark-300',
                        sizeClasses[size]
                      );
                      shield.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gold-400"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`;
                      parent.replaceChild(shield, target);
                    }
                  }}
                />
              ) : (
                <div
                  className={cn('flex items-center justify-center bg-dark-300', sizeClasses[size])}
                >
                  <Shield className="h-2/3 w-2/3 text-gold-400" />
                </div>
              )}
            </div>
          </Link>
        </Tippy>
      ))}

      {hasMore && (
        <Tippy
          content={showTooltip ? `${sortedClubs.length - limit} more clubs` : null}
          delay={[100, 0]}
          placement="top"
        >
          <div
            className={cn(
              'flex items-center justify-center rounded-full bg-dark-300 text-xs text-white',
              borderClasses[size],
              sizeClasses[size]
            )}
          >
            +{sortedClubs.length - limit}
          </div>
        </Tippy>
      )}
    </div>
  );
}
